import React, { useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Popover, PopoverInteractionKind } from "@blueprintjs/core";
import { N_A } from "shared/rsDictionary";
import { getScoreColor } from "shared/colors";
import Loader from "components/Loader";
import HtmlText from "components/HtmlText";
import ExclamationMarkIcon from "icons/exclamation-mark.svg";
import * as colors from "styles/colorValues.module.scss";
import * as styles from "./styles.module.scss";
export const getTextColor = (score, highlighted, highlightMode, hasBiasedValue) => {
    if (hasBiasedValue) {
        return highlighted ? colors.red : colors.s600;
    }
    if (!highlighted) {
        return colors.s600;
    }
    if (highlightMode) {
        if (!highlightMode.highlightByInput) {
            return colors.s200;
        }
        return isNaN(Number(score)) ? colors.white : colors.black;
    }
    return isNaN(Number(score)) || Math.round(score) < 60 ? colors.white : colors.black;
};
const ScoreIcon = ({ score, errorMessage, errored = false, pending = false, highlighted = false, hasBiasedValue = false, className, tooltip, highlightMode, onClick, testId, }) => {
    const rootRef = useRef(null);
    const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
    const backgroundColor = useMemo(() => {
        if (hasBiasedValue) {
            return colors.white;
        }
        if (!highlighted) {
            return colors.s200;
        }
        const statusColor = getScoreColor(Math.round(Number(score)));
        if (highlightMode) {
            return highlightMode.highlightByInput ? statusColor : colors.aw10;
        }
        return statusColor;
    }, [highlightMode, highlighted, score, hasBiasedValue]);
    const rootElementsProps = useMemo(() => ({
        onClick: (e) => {
            e.stopPropagation();
            tooltip && setTooltipIsOpen(!tooltipIsOpen);
        },
        onBlur: () => tooltipIsOpen && setTooltipIsOpen(false),
        "data-testid": testId,
    }), [testId, tooltip, tooltipIsOpen]);
    const onClickTooltipProps = useMemo(() => tooltip
        ? {
            isOpen: tooltipIsOpen,
            onInteraction: (nextOpenState) => tooltipIsOpen && setTooltipIsOpen(nextOpenState),
        }
        : {}, [tooltipIsOpen, tooltip]);
    const content = useMemo(() => {
        if (pending) {
            return (React.createElement("div", { className: classNames(styles.loaderWrapper, className) },
                React.createElement(Loader, { className: styles.loader })));
        }
        if (errored) {
            return (React.createElement("div", { className: classNames(styles.root, styles.error, className, {
                    [styles.transparent]: !!highlightMode && !highlightMode.highlightByInput,
                }), ...rootElementsProps },
                React.createElement(ExclamationMarkIcon, { className: "score-error" })));
        }
        return (React.createElement("div", { className: classNames(styles.root, { [styles.highlighted]: !!highlighted }, className, {
                [styles.dishighlightByInput]: highlightMode && !highlightMode.highlightByInput,
                [styles.hasBiasedValue]: hasBiasedValue,
            }), style: {
                backgroundColor,
                color: getTextColor(score, highlighted, highlightMode, hasBiasedValue),
            }, ...rootElementsProps },
            isNaN(Number(score)) ? N_A : Math.round(Number(score)),
            hasBiasedValue && "!"));
    }, [
        errored,
        pending,
        className,
        highlighted,
        highlightMode,
        hasBiasedValue,
        backgroundColor,
        score,
        rootElementsProps,
    ]);
    if (!tooltip && !errorMessage) {
        return React.createElement("div", { onClick: onClick }, content);
    }
    return (React.createElement("div", { ref: rootRef, onClick: onClick },
        React.createElement(Popover, { fill: true, minimal: true, content: React.createElement("div", { className: classNames(styles.content, { [styles.shifted]: !!errorMessage }), onClick: (e) => e.stopPropagation() }, errorMessage ? React.createElement(HtmlText, { className: styles.errorMessage, text: errorMessage }) : tooltip), placement: "bottom", portalClassName: styles.popoverPortal, popoverClassName: styles.popover, transitionDuration: 20, interactionKind: tooltip ? PopoverInteractionKind.CLICK : PopoverInteractionKind.HOVER, ...onClickTooltipProps }, content)));
};
export default ScoreIcon;
